import { observer } from 'mobx-react'
import { UserType, XLSX_UserRow } from 'types'
import React from 'react'
import { Container } from '@mui/material'
import { UploadXLSX } from './uploadXLSX'
import { useTranslation } from 'react-i18next'
import { User } from 'models'
import { DataGridStyles, Modal, SnackBarContext } from 'components'
import { DataGrid, GridColumns } from '@mui/x-data-grid'
import { Loading } from 'views/loading'


export interface IUserImportProps {
  open: boolean,
  onClose(): void,
  onAddUsers(users: User[]): void,
}

export const UserImport = observer((props: IUserImportProps): JSX.Element => {
  const { onClose, open, onAddUsers } = props;

  const [users,        setUsers]        = React.useState<XLSX_UserRow[]>(null);
  const [invalidUsers, setInvalidUsers] = React.useState<XLSX_UserRow[]>(null);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const { toast }   = React.useContext(SnackBarContext);
  const { t } = useTranslation();

  const bulkImportParticipants = async (users: XLSX_UserRow[]): Promise<boolean> => {
    setIsProcessing(true);
    const addedUsers:   User[] = [];
    const invalidUsers: XLSX_UserRow[] = [];
    for(const u of users) {
      try {
        if(isNaN(Number(u.personal_number)) || u.personal_number.toString().length != 12) 
          throw new Error("Invalid personal number");

        const newUser: User = new User({
          ...u,
          password: 'password',
          user_type: UserType.Participant,
        });
        await newUser.save(undefined, true);
        addedUsers.push(newUser);
      } catch (error) {
        u.id = invalidUsers ? invalidUsers.length : 0; 
        invalidUsers.push(u);
      }
    }
    setIsProcessing(false);
    onAddUsers(addedUsers);
    if(!invalidUsers.length) {
      toast(t("successfully_added_users", {count: addedUsers.length}), "success");
      onClose();
      return true;
    } else {
      toast(t("error_creating_users", {count: invalidUsers.length}), "error");
      setInvalidUsers(invalidUsers);
      return false;
    }
  };

  React.useEffect(() => {
    if(users) {
      try {
        bulkImportParticipants(users);
      } catch (err) {
        console.error('bulkImportParticipants err', err);
        toast(t('error_import_users'), "error");
      }
    }
  }, [users]);

  const invalid_columns: GridColumns = [
    "personal_number",
    "email",
    "first_name",
    "last_name",
    "title",
    "phone"
  ].map(k => ({
    flex:              1,
    field:             k,
    headerName:        t(k),
    headerClassName:   'columnHeader',
    headerAlign:       'left',
    align:             'left',
    sortable:          false,
    filterable:        false,
    disableColumnMenu: true,
    editable:          false,

  }));

  let content: React.ReactNode;
  if(isProcessing) content = (
    <Modal
    fullWidth 
    maxWidth = 'sm'
    open     = {open}
    onClose  = {() => { 
      setUsers(null); 
      setInvalidUsers(null); 
      setIsProcessing(false);
      onClose();
    }}
    >
      <Loading/>
    </Modal>
  );
  else if(invalidUsers) content = (
    <Modal
    fullWidth 
    title    = {t("import_users_modal_title")}
    maxWidth = 'md'
    open     = {open}
    onClose  = {() => { 
      setUsers(null); 
      setInvalidUsers(null); 
      setIsProcessing(false);
      onClose();
    }}>
    <Container maxWidth="lg">
      <DataGridStyles>
        <DataGrid
          autoHeight
          disableColumnSelector
          disableSelectionOnClick
          rowHeight              = {66}
          density                = 'standard'
          rows                   = {invalidUsers}
          columns                = {invalid_columns}
          pageSize               = {5}
          rowsPerPageOptions     = {[5]}
          hideFooterPagination   = {invalidUsers.length < 5}
        />
      </DataGridStyles>
    </Container>
    </Modal>
  );
  
  else content = (
    <Modal
    fullWidth 
    maxWidth = 'sm'
    title    = {t("import_users_modal_title")}
    open     = {open}
    onClose  = {() => { 
      setUsers(null); 
      setInvalidUsers(null); 
      setIsProcessing(false);
      onClose();
    }}>
    <UploadXLSX
      onUploadComplete = {(users) => setUsers(users)}
      onError          = {onClose}
    />
    </Modal>
  );

  return content;
});